/**
 * v1.3.71 — Native disk cache for the Xtream pre-warm bundle.
 *
 * On Capacitor Android, IndexedDB writes for the 4-5 MB bundle
 * round-trip through the WebView's IDB layer (~300-500 ms on a
 * mid-range Android TV). Capacitor Filesystem talks directly to the
 * Android file API → typical write/read of the same bundle is
 * ~30-50 ms.
 *
 * Web build (Electron / browser) falls back to a transparent no-op.
 *
 * Implementation note: we deliberately avoid `import { Capacitor }
 * from "@capacitor/core"` because that package isn't a dependency of
 * the web frontend (it's pulled in transitively by the native
 * `@capacitor/filesystem` only when running inside the APK). At
 * runtime in the WebView, Capacitor exposes itself via the global
 * `window.Capacitor` — we detect that, then dynamic-import the
 * Filesystem plugin inside a try-block so the web bundle compiles
 * without the optional dep.
 */

const FILENAME = (playlistId) => `prewarm_${playlistId}.json`;

let _fs = null;
let _fsTried = false;

async function _getFs() {
  if (_fsTried) return _fs;
  _fsTried = true;
  if (
    typeof window === "undefined" ||
    !window.Capacitor ||
    typeof window.Capacitor.isNativePlatform !== "function" ||
    !window.Capacitor.isNativePlatform()
  ) {
    return null;
  }
  try {
    // Webpack honours /* webpackIgnore: true */ but CRA may still
    // try to resolve the module at build time, which fails the
    // browser bundle. We work around it by using a string variable
    // for the path — the static analyser bails out and treats it as
    // a true runtime import (resolved only when Capacitor is present).
    const moduleName = "@capacitor" + "/filesystem";
    // eslint-disable-next-line no-eval
    const mod = await import(/* webpackIgnore: true */ moduleName);
    _fs = { Filesystem: mod.Filesystem, Directory: mod.Directory };
    return _fs;
  } catch {
    return null;
  }
}

/**
 * Read the cached pre-warm bundle for `playlistId` from native disk.
 * Returns `null` on any failure (no cache, decode error, plugin
 * missing, browser environment). Best-effort.
 */
export async function readPrewarmBundleFromDisk(playlistId) {
  if (!playlistId) return null;
  const fs = await _getFs();
  if (!fs) return null;
  try {
    const res = await fs.Filesystem.readFile({
      path: FILENAME(playlistId),
      directory: fs.Directory.Data,
      encoding: "utf8",
    });
    if (!res || !res.data) return null;
    return JSON.parse(res.data);
  } catch {
    return null;
  }
}

/** Persist the bundle to native disk. Best-effort. */
export async function writePrewarmBundleToDisk(playlistId, bundle) {
  if (!playlistId || !bundle) return false;
  const fs = await _getFs();
  if (!fs) return false;
  try {
    await fs.Filesystem.writeFile({
      path: FILENAME(playlistId),
      directory: fs.Directory.Data,
      encoding: "utf8",
      data: JSON.stringify(bundle),
    });
    return true;
  } catch {
    return false;
  }
}

/** Delete the cached bundle (called after admin force-refresh). */
export async function deletePrewarmBundleFromDisk(playlistId) {
  if (!playlistId) return false;
  const fs = await _getFs();
  if (!fs) return false;
  try {
    await fs.Filesystem.deleteFile({
      path: FILENAME(playlistId),
      directory: fs.Directory.Data,
    });
    return true;
  } catch {
    return false;
  }
}
